// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import { Box, Card, Divider, Group, Text } from '@mantine/core';
import { formatHumanName } from '@medplum/core';
import type { Patient, QuestionnaireResponseItem } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import {
  CLOUDPAT_OBSERVATION_SYSTEM,
  calculateBmiFromStopBangQR,
  calculateEpworthScore,
  calculateStopBangScoreFromQR,
  getMrn,
  TRIAGE_OBS_CODES,
} from '@polarissleep/shared';
import * as Sentry from '@sentry/react';
import dayjs from 'dayjs';
import type { JSX } from 'react';
import { type KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { PRONOUN_OPTIONS } from '../../constants/patient-profile';
import { type Treatment, treatmentFields, usePatientTreatments } from '../../hooks/usePatientTreatments';
import { getPronouns } from '../../utils/patient-extensions';
import {
  createBloodPressureObservation,
  createHeartRateObservation,
  createRespiratoryRateObservation,
  getLatestBloodPressure,
  getLatestHeartRate,
  getLatestHeightCm,
  getLatestRespiratoryRate,
  getLatestWeightKg,
} from '../../utils/patient-vitals';
import { saveTreatmentCarePlan } from '../../utils/treatment-care-plans';
import classes from './PatientVitalsSummary.module.css';

interface PatientVitals {
  heightCm?: number;
  weightKg?: number;
  bmi?: number;
  ess?: number;
  stopBang?: number;
  ahi?: number;
  bloodPressure?: string;
  heartRate?: number;
  respiratoryRate?: number;
}

const EMPTY_VITALS: PatientVitals = {};

async function getLatestMetric(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string,
  system: string,
  code: string
): Promise<number | undefined> {
  try {
    const observation = await medplum.searchOne('Observation', {
      subject: `Patient/${patientId}`,
      category: 'http://polarissleep.com/codes/triage-observation-category|patient-latest-triage',
      code: `${system}|${code}`,
      _sort: '-date',
      _count: '1',
    });
    return observation?.valueQuantity?.value;
  } catch {
    return undefined;
  }
}

async function getLatestQuestionnaireScore(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string,
  questionnaireName: string
): Promise<{ score?: number; bmi?: number }> {
  try {
    const questionnaire = await medplum.searchOne('Questionnaire', { name: questionnaireName });
    if (!questionnaire?.id) {
      return {};
    }
    const response = await medplum.searchOne('QuestionnaireResponse', {
      subject: `Patient/${patientId}`,
      questionnaire: `Questionnaire/${questionnaire.id}`,
      _sort: '-authored',
      _count: '1',
    });
    if (!response) {
      return {};
    }
    if (questionnaireName === 'stop-bang') {
      return { score: calculateStopBangScoreFromQR(response), bmi: calculateBmiFromStopBangQR(response) };
    }
    const answers = Object.fromEntries(
      (response.item ?? []).flatMap((item: QuestionnaireResponseItem) =>
        item.linkId ? [[item.linkId, item.answer?.[0] ?? {}]] : []
      )
    );
    return { score: calculateEpworthScore(answers) };
  } catch {
    return {};
  }
}

export async function getLatestAhi(
  medplum: ReturnType<typeof useMedplum>,
  patientId: string
): Promise<number | undefined> {
  try {
    for (const code of ['AH', 'AHI']) {
      const observation = await medplum.searchOne('Observation', {
        subject: `Patient/${patientId}`,
        code: `${CLOUDPAT_OBSERVATION_SYSTEM}|${code}`,
        _sort: '-date',
        _count: '1',
      });
      if (observation?.valueQuantity?.value !== undefined) {
        return observation.valueQuantity.value;
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

export function usePatientVitals(patient: Patient | undefined): {
  vitals: PatientVitals;
  loading: boolean;
  refresh: () => Promise<void>;
} {
  const medplum = useMedplum();
  const [vitals, setVitals] = useState<PatientVitals>(EMPTY_VITALS);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async (): Promise<void> => {
    if (!patient?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all([
      getLatestHeightCm(medplum, patient.id).catch(() => undefined),
      getLatestWeightKg(medplum, patient.id).catch(() => undefined),
      getLatestMetric(medplum, patient.id, TRIAGE_OBS_CODES.epworth.system, TRIAGE_OBS_CODES.epworth.code),
      getLatestMetric(medplum, patient.id, TRIAGE_OBS_CODES['stop-bang'].system, TRIAGE_OBS_CODES['stop-bang'].code),
      getLatestMetric(medplum, patient.id, TRIAGE_OBS_CODES.bmi.system, TRIAGE_OBS_CODES.bmi.code),
      getLatestQuestionnaireScore(medplum, patient.id, 'epworth-sleepiness-scale'),
      getLatestQuestionnaireScore(medplum, patient.id, 'stop-bang'),
      getLatestAhi(medplum, patient.id),
      getLatestBloodPressure(medplum, patient.id).catch(() => undefined),
      getLatestHeartRate(medplum, patient.id).catch(() => undefined),
      getLatestRespiratoryRate(medplum, patient.id).catch(() => undefined),
    ])
      .then(
        ([heightCm, weightKg, ess, stopBang, bmi, essQr, stopBangQr, ahi, bloodPressure, heartRate, respiratoryRate]) =>
          setVitals({
            heightCm,
            weightKg,
            ess: ess ?? essQr.score,
            stopBang: stopBang ?? stopBangQr.score,
            bmi:
              bmi ??
              stopBangQr.bmi ??
              (heightCm !== undefined && weightKg !== undefined && heightCm > 0
                ? Math.round((weightKg / ((heightCm / 100) * (heightCm / 100))) * 10) / 10
                : undefined),
            ahi,
            bloodPressure:
              bloodPressure?.systolic !== undefined && bloodPressure.diastolic !== undefined
                ? `${bloodPressure.systolic}/${bloodPressure.diastolic}`
                : undefined,
            heartRate,
            respiratoryRate,
          })
      )
      .catch(() => setVitals(EMPTY_VITALS))
      .finally(() => setLoading(false));
  }, [medplum, patient?.id]);

  useEffect(() => {
    refresh().catch(() => setVitals(EMPTY_VITALS));
  }, [refresh]);

  return { vitals, loading, refresh };
}

function getEssDetail(score: number | undefined): string | undefined {
  if (score === undefined) {
    return undefined;
  }
  return score >= 10 ? 'Elevated' : 'Mild';
}

function getStopBangDetail(score: number | undefined): string | undefined {
  if (score === undefined) {
    return undefined;
  }
  if (score >= 5) {
    return 'High risk';
  }
  return score >= 3 ? 'Intermediate' : 'Low risk';
}

function getAhiDetail(ahi: number | undefined): string | undefined {
  if (ahi === undefined) {
    return undefined;
  }
  if (ahi < 5) {
    return 'Normal';
  }
  if (ahi < 15) {
    return 'Mild';
  }
  return ahi < 30 ? 'Moderate' : 'Severe OSA';
}

function getBmiDetail(bmi: number | undefined): string | undefined {
  if (bmi === undefined) {
    return undefined;
  }
  if (bmi < 18.5) {
    return 'Underweight';
  }
  if (bmi < 25) {
    return 'Normal';
  }
  if (bmi < 30) {
    return 'Overweight';
  }
  if (bmi < 35) {
    return 'Obese I';
  }
  if (bmi < 40) {
    return 'Obese II';
  }
  return 'Obese III';
}

function getBmiTone(bmi: number | undefined): string | undefined {
  if (bmi === undefined || bmi < 25) {
    return undefined;
  }
  return bmi >= 35 ? 'red' : 'yellow';
}

function getBloodPressureSystolic(bloodPressure: string | undefined): number | undefined {
  const systolic = Number(bloodPressure?.split('/')[0]);
  return Number.isFinite(systolic) ? systolic : undefined;
}

function getBloodPressureDetail(bloodPressure: string | undefined): string | undefined {
  const systolic = getBloodPressureSystolic(bloodPressure);
  if (systolic === undefined) {
    return undefined;
  }
  if (systolic >= 140) {
    return 'Stage 2';
  }
  if (systolic >= 130) {
    return 'Stage 1';
  }
  if (systolic >= 120) {
    return 'Elevated';
  }
  return 'Normal';
}

function getBloodPressureTone(bloodPressure: string | undefined): string | undefined {
  const systolic = getBloodPressureSystolic(bloodPressure);
  if (systolic === undefined) {
    return undefined;
  }
  if (systolic >= 140) {
    return 'red';
  }
  return systolic >= 120 ? 'yellow' : undefined;
}

function getHeartRateDetail(heartRate: number | undefined): string | undefined {
  if (heartRate === undefined) {
    return undefined;
  }
  if (heartRate < 50) {
    return 'Bradycardic';
  }
  return heartRate > 100 ? 'Tachycardic' : undefined;
}

function getRespiratoryRateDetail(respiratoryRate: number | undefined): string | undefined {
  if (respiratoryRate === undefined) {
    return undefined;
  }
  if (respiratoryRate < 12) {
    return 'Low';
  }
  return respiratoryRate > 20 ? 'High' : undefined;
}

function getAbnormalRateTone(rate: number | undefined, low: number, high: number): string | undefined {
  return rate !== undefined && (rate < low || rate > high) ? 'yellow' : undefined;
}

function getLatestTreatment(treatments: Treatment[]): Treatment | undefined {
  return treatments.slice().sort((a, b) => b.start.localeCompare(a.start))[0];
}

function getTreatmentLabel(treatment: Treatment | undefined, loading: boolean): string {
  if (loading) {
    return 'Loading…';
  }
  if (!treatment) {
    return 'Not started';
  }
  if (treatment.modality === 'Other' && 'other' in treatment && treatment.other) {
    return treatment.other;
  }
  return treatment.modality;
}

function Metric({
  label,
  value,
  suffix,
  detail,
  tone,
}: {
  label: string;
  value?: string;
  suffix?: string;
  detail?: string;
  tone?: string;
}): JSX.Element {
  let toneClass = '';
  if (tone === 'red') {
    toneClass = classes.metricRed;
  } else if (tone === 'yellow') {
    toneClass = classes.metricYellow;
  }
  const widthClass = getMetricWidthClass(label);
  return (
    <Box className={`${classes.metric} ${widthClass} ${toneClass}`}>
      <div className={classes.metricRow}>
        <span className={classes.metricLabel}>{label}</span>
        <span className={classes.metricValue}>{value ?? '—'}</span>
        {suffix && value && <span className={classes.metricUnit}>{suffix}</span>}
      </div>
      {detail && <div className={classes.metricDetail}>{detail}</div>}
    </Box>
  );
}

function getMetricWidthClass(label: string): string {
  if (label === 'ESS' || label === 'HR' || label === 'RR') {
    return classes.metricStandard;
  }
  if (label === 'BMI') {
    return classes.metricBmi;
  }
  if (label === 'BP') {
    return classes.metricBloodPressure;
  }
  if (label === 'STOP-BANG') {
    return classes.metricWide;
  }
  if (label === 'AHI') {
    return classes.metricAhi;
  }
  return '';
}

type EditableMetricProps = {
  label: string;
  value?: string;
  suffix?: string;
  detail?: string;
  tone?: string;
  onEdit: () => void;
};

function EditableMetric({ label, value, suffix, detail, tone, onEdit }: EditableMetricProps): JSX.Element {
  let toneClass = '';
  if (tone === 'red') {
    toneClass = classes.metricRed;
  } else if (tone === 'yellow') {
    toneClass = classes.metricYellow;
  }
  const widthClass = getMetricWidthClass(label);
  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onEdit();
    }
  };
  return (
    <Box
      className={`${classes.metric} ${widthClass} ${classes.metricEditable} ${toneClass}`}
      role="button"
      tabIndex={0}
      onClick={onEdit}
      onKeyDown={handleKeyDown}
    >
      <div className={classes.metricRow}>
        <span className={classes.metricLabel}>{label}</span>
        <span className={classes.metricValue}>{value ?? '—'}</span>
        {suffix && value && <span className={classes.metricUnit}>{suffix}</span>}
        <span className={classes.metricPencil}>✎</span>
      </div>
      {detail && <div className={classes.metricDetail}>{detail}</div>}
    </Box>
  );
}

export function PatientVitalsSummary({
  patient,
  compact = false,
  variant = 'card',
}: {
  patient: Patient;
  compact?: boolean;
  variant?: 'card' | 'header' | 'encounter';
}): JSX.Element {
  const { vitals, loading, refresh: refreshVitals } = usePatientVitals(patient);
  const medplum = useMedplum();
  const { treatments, loading: treatmentsLoading, refresh: refreshTreatments } = usePatientTreatments(patient);
  const latestTreatment = getLatestTreatment(treatments);
  const [editing, setEditing] = useState<'treatment' | 'bp' | 'hr' | 'rr' | undefined>();
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);

  const startEdit = (field: 'treatment' | 'bp' | 'hr' | 'rr'): void => {
    let value = '';
    if (field === 'treatment') {
      value = latestTreatment?.start ?? '';
    } else if (field === 'bp') {
      value = vitals.bloodPressure ?? '';
    } else if (field === 'hr') {
      value = vitals.heartRate?.toString() ?? '';
    } else {
      value = vitals.respiratoryRate?.toString() ?? '';
    }
    setEditing(field);
    setDraft(value);
  };

  const cancelEdit = (): void => {
    setEditing(undefined);
    setDraft('');
  };

  const saveEdit = async (): Promise<void> => {
    if (!editing || saving) {
      return;
    }
    const value = draft.trim();
    if (editing === 'treatment') {
      if (!/^\d{4}-\d{2}-\d{2}$/.test(value) || !latestTreatment) {
        return;
      }
    } else if (editing === 'bp') {
      if (!/^(\d{2,3})\/(\d{2,3})$/.test(value)) {
        return;
      }
    } else if (!/^\d+(\.\d+)?$/.test(value) || Number(value) <= 0) {
      return;
    }

    setSaving(true);
    try {
      if (editing === 'treatment' && latestTreatment) {
        await saveTreatmentCarePlan(
          medplum,
          patient.id ?? '',
          { ...treatmentFields(latestTreatment), start: value },
          { existing: latestTreatment.carePlan, legacyKey: latestTreatment.legacyKey }
        );
        await refreshTreatments();
      } else if (editing === 'bp') {
        const [systolic, diastolic] = value.split('/').map(Number);
        await medplum.createResource(createBloodPressureObservation(patient, systolic, diastolic));
        await refreshVitals();
      } else if (editing === 'hr') {
        await medplum.createResource(createHeartRateObservation(patient, Number(value)));
        await refreshVitals();
      } else if (editing === 'rr') {
        await medplum.createResource(createRespiratoryRateObservation(patient, Number(value)));
        await refreshVitals();
      }
      cancelEdit();
    } catch (error) {
      Sentry.captureException(error);
    } finally {
      setSaving(false);
    }
  };

  const handleEditorKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (event.key === 'Enter') {
      event.preventDefault();
      saveEdit().catch(() => undefined);
    } else if (event.key === 'Escape') {
      event.preventDefault();
      cancelEdit();
    }
  };

  const editableInput = (field: 'treatment' | 'bp' | 'hr' | 'rr'): JSX.Element => (
    <Box className={classes.metricEditing}>
      <input
        autoFocus
        type={field === 'treatment' ? 'date' : 'text'}
        inputMode={field === 'bp' ? 'text' : 'decimal'}
        value={draft}
        onChange={(event) => setDraft(event.currentTarget.value)}
        onKeyDown={handleEditorKeyDown}
        disabled={saving}
        aria-label={`Edit ${field}`}
      />
      <span>Enter to save · Esc to cancel</span>
    </Box>
  );

  const scores = (
    <Group gap="sm" wrap="wrap">
      <Metric
        label="ESS"
        value={loading ? '…' : vitals.ess?.toString()}
        suffix="/24"
        detail={getEssDetail(vitals.ess)}
        tone={vitals.ess !== undefined && vitals.ess >= 10 ? 'yellow' : undefined}
      />
      <Metric
        label="STOP-BANG"
        value={loading ? '…' : vitals.stopBang?.toString()}
        suffix="/8"
        detail={getStopBangDetail(vitals.stopBang)}
        tone={vitals.stopBang !== undefined && vitals.stopBang >= 5 ? 'red' : undefined}
      />
    </Group>
  );
  const measurements = (
    <Group gap="sm" wrap="wrap">
      <Metric
        label="BMI"
        value={loading ? '…' : vitals.bmi?.toFixed(1)}
        detail={getBmiDetail(vitals.bmi)}
        tone={getBmiTone(vitals.bmi)}
      />
      {editing === 'bp' ? (
        editableInput('bp')
      ) : (
        <EditableMetric
          label="BP"
          value={loading ? '…' : vitals.bloodPressure}
          suffix="mmHg"
          detail={getBloodPressureDetail(vitals.bloodPressure)}
          tone={getBloodPressureTone(vitals.bloodPressure)}
          onEdit={() => startEdit('bp')}
        />
      )}
      {editing === 'hr' ? (
        editableInput('hr')
      ) : (
        <EditableMetric
          label="HR"
          value={loading ? '…' : vitals.heartRate?.toString()}
          suffix="bpm"
          detail={getHeartRateDetail(vitals.heartRate)}
          tone={getAbnormalRateTone(vitals.heartRate, 50, 100)}
          onEdit={() => startEdit('hr')}
        />
      )}
      {editing === 'rr' ? (
        editableInput('rr')
      ) : (
        <EditableMetric
          label="RR"
          value={loading ? '…' : vitals.respiratoryRate?.toString()}
          suffix="/min"
          detail={getRespiratoryRateDetail(vitals.respiratoryRate)}
          tone={getAbnormalRateTone(vitals.respiratoryRate, 12, 20)}
          onEdit={() => startEdit('rr')}
        />
      )}
    </Group>
  );

  if (variant === 'header') {
    return (
      <Box className={classes.headerSummary} mt="md">
        <Text className={classes.sectionLabel} mb="xs">
          Screening scores
        </Text>
        {scores}
        <Divider my="md" />
        <Text className={classes.sectionLabel} mb="xs">
          Vitals
        </Text>
        {measurements}
      </Box>
    );
  }

  if (variant === 'encounter') {
    const name = patient.name?.[0] ? formatHumanName(patient.name[0]) : 'Unnamed patient';
    const pronouns = getPronouns(patient)
      .map((code) => PRONOUN_OPTIONS.find((option) => option.value === code)?.label)
      .filter(Boolean)
      .join(' / ');
    const gender = patient.gender ? `${patient.gender[0].toUpperCase()}${patient.gender.slice(1)}` : undefined;
    const age = patient.birthDate ? dayjs().diff(dayjs(patient.birthDate), 'year') : undefined;
    return (
      <Card className={`${classes.card} ${classes.identityCard}`} withBorder>
        <Text className={classes.identityName}>{name}</Text>
        <Text className={classes.identityMeta} mt={2}>
          DOB{' '}
          <Text span c="dark" fw={600}>
            {patient.birthDate ? dayjs(patient.birthDate).format('MMM D, YYYY') : '—'}
          </Text>
          {age !== undefined && ` (${age} y)`} · MRN{' '}
          <Text span c="dark" fw={600}>
            {getMrn(patient) ?? '—'}
          </Text>
          {gender && ` · ${gender}`}
          {pronouns && ` · ${pronouns}`}
        </Text>
        <Divider my="md" />
        <Text className={classes.sectionLabel} mb="xs">
          Screener scores
        </Text>
        <Group gap="sm" wrap="wrap">
          <Metric
            label="ESS"
            value={loading ? '…' : vitals.ess?.toString()}
            suffix="/24"
            detail={getEssDetail(vitals.ess)}
            tone={vitals.ess !== undefined && vitals.ess >= 10 ? 'yellow' : undefined}
          />
          <Metric
            label="STOP-BANG"
            value={loading ? '…' : vitals.stopBang?.toString()}
            suffix="/8"
            detail={getStopBangDetail(vitals.stopBang)}
            tone={vitals.stopBang !== undefined && vitals.stopBang >= 5 ? 'red' : undefined}
          />
          <Metric
            label="AHI"
            value={loading ? '…' : vitals.ahi?.toFixed(1)}
            suffix="/hr"
            detail={getAhiDetail(vitals.ahi)}
            tone={vitals.ahi !== undefined && vitals.ahi >= 30 ? 'red' : undefined}
          />
        </Group>
      </Card>
    );
  }

  return (
    <Card className={classes.card} withBorder radius="md" p={compact ? 'sm' : 'md'}>
      <Text className={classes.sectionLabel} mb="xs">
        Treatment
      </Text>
      {editing === 'treatment' ? (
        editableInput('treatment')
      ) : (
        <Box
          className={`${classes.treatmentRow} ${latestTreatment ? classes.treatmentEditable : ''}`}
          onClick={() => latestTreatment && startEdit('treatment')}
        >
          <Text className={classes.treatmentLabel}>Treatment initiation</Text>
          <Text className={classes.treatmentValue}>{getTreatmentLabel(latestTreatment, treatmentsLoading)}</Text>
          {latestTreatment && (
            <Text className={classes.treatmentDate}>
              {dayjs(latestTreatment.start).format('MMM D, YYYY')}
              {' · Day '}
              {dayjs().diff(dayjs(latestTreatment.start), 'day') + 1}
            </Text>
          )}
          {latestTreatment && <span className={classes.metricPencil}>✎</span>}
        </Box>
      )}
      <Divider my="md" />
      <Text className={classes.sectionLabel} mb="xs">
        Screener scores
      </Text>
      <Group gap="sm" wrap="wrap">
        <Metric
          label="ESS"
          value={loading ? '…' : vitals.ess?.toString()}
          suffix="/24"
          detail={getEssDetail(vitals.ess)}
          tone={vitals.ess !== undefined && vitals.ess >= 10 ? 'yellow' : undefined}
        />
        <Metric
          label="STOP-BANG"
          value={loading ? '…' : vitals.stopBang?.toString()}
          suffix="/8"
          detail={getStopBangDetail(vitals.stopBang)}
          tone={vitals.stopBang !== undefined && vitals.stopBang >= 5 ? 'red' : undefined}
        />
      </Group>
      <Divider my="md" />
      <Text className={classes.sectionLabel} mb="xs">
        Most recent test results
      </Text>
      <Group gap="sm" wrap="wrap">
        <Metric
          label="AHI"
          value={loading ? '…' : vitals.ahi?.toFixed(1)}
          suffix="/hr"
          detail={getAhiDetail(vitals.ahi)}
          tone={vitals.ahi !== undefined && vitals.ahi >= 30 ? 'red' : undefined}
        />
      </Group>
      <Divider my="md" />
      <Text className={classes.sectionLabel} mb="xs">
        Vitals
      </Text>
      {measurements}
    </Card>
  );
}

export type { PatientVitals };
